import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Search, Plus, Pencil, Trash2, Loader2, UserCog } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { admin: "Admin", sekretaris: "Sekretaris", ustadz: "Ustadz" };
const ROLE_BADGE = {
  admin: "bg-emerald-100 text-emerald-800",
  sekretaris: "bg-sky-100 text-sky-800",
  ustadz: "bg-amber-100 text-amber-800",
};

function emptyForm() {
  return { email: "", name: "", role: "sekretaris", password: "", is_active: true };
}

export default function PenggunaPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (roleFilter !== "all") params.role = roleFilter;
      const { data } = await api.get("/users", { params });
      setItems(data.items);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      email: it.email, name: it.name, role: it.role, password: "", is_active: it.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, is_active: form.is_active };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Pengguna diperbarui");
      } else {
        await api.post("/users", {
          email: form.email.toLowerCase().trim(),
          name: form.name, role: form.role, password: form.password,
        });
        toast.success("Pengguna ditambahkan");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success("Pengguna dihapus");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="pengguna-page">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Sistem</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Manajemen Pengguna</h1>
        <p className="text-slate-500 text-sm">Kelola akun & hak akses pengguna sistem.</p>
      </div>

      <Card className="p-4 md:p-5 border border-slate-200 shadow-sm bg-white">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input placeholder="Cari nama atau email..." value={q} onChange={(e) => setQ(e.target.value)}
                   className="pl-9 h-10" data-testid="pengguna-search-input" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full md:w-44 h-10" data-testid="pengguna-role-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Peran</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="sekretaris">Sekretaris</SelectItem>
              <SelectItem value="ustadz">Ustadz</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 gap-2"
                  data-testid="add-pengguna-button">
            <Plus className="h-4 w-4" /> Tambah Pengguna
          </Button>
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nama</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Peran</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500 font-semibold">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">
                  <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                </TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">
                  Belum ada pengguna.
                </TableCell></TableRow>
              ) : items.map((it) => (
                <TableRow key={it.id} data-testid={`pengguna-row-${it.id}`} className="hover:bg-emerald-50/40">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-violet-50 text-violet-700 flex items-center justify-center">
                        <UserCog className="h-4 w-4" />
                      </div>
                      <span className="font-semibold text-slate-900">{it.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{it.email}</TableCell>
                  <TableCell>
                    <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full uppercase tracking-wider ${ROLE_BADGE[it.role]}`}>
                      {ROLE_LABEL[it.role]}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                      it.is_active !== false ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"
                    }`}>
                      {it.is_active !== false ? "Aktif" : "Nonaktif"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`edit-pengguna-${it.id}`}>
                      <Pencil className="h-4 w-4 text-slate-600" />
                    </Button>
                    {it.id !== user.id && (
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(it)} data-testid={`delete-pengguna-${it.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Pengguna" : "Tambah Pengguna"}</DialogTitle>
            <DialogDescription>Isi informasi akun pengguna.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input required type="email" disabled={!!editing}
                     value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                     data-testid="pengguna-form-email" />
            </div>
            <div>
              <Label>Nama Lengkap *</Label>
              <Input required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                     data-testid="pengguna-form-nama" />
            </div>
            <div>
              <Label>Peran *</Label>
              <Select value={form.role} onValueChange={(v) => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="pengguna-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="sekretaris">Sekretaris</SelectItem>
                  <SelectItem value="ustadz">Ustadz</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{editing ? "Kata Sandi Baru (opsional)" : "Kata Sandi *"}</Label>
              <Input type="password" required={!editing} value={form.password}
                     onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                     data-testid="pengguna-form-password" />
            </div>
            {editing && (
              <div className="flex items-center gap-3">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm(f => ({ ...f, is_active: v }))}
                        data-testid="pengguna-form-active" />
                <Label>Akun Aktif</Label>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="pengguna-form-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Pengguna?</AlertDialogTitle>
            <AlertDialogDescription>
              Akun <span className="font-semibold">{deleteTarget?.name}</span> akan dihapus permanen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="pengguna-delete-confirm"
                               className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
